import StorageHelper from '@/Utility/StorageHelper';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Utilities from '@/Utility/Utilities';
import LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import ComponentManager from '@/Management/ComponentManager';
export default class ListViewHelper {
    constructor(optionalDisposable?: any) {}

    /**
     * Lädt die Spaltenkonfiguration für den angegebenen Schlüssel und mergt sie mit der aktuellen Spaltendefinition vom Server
     * @param storageColumnDefinitionKey
     * @param columns
     * @param updateCache Gibt an, ob der neu kalkulierte Werte im Cache gespeichert werden soll
     */
    public static loadColumnDefinitions<T extends CoreFrameDTOs.IColumnDTO>(storageColumnDefinitionKey: string, columns: Array<T>, extendedColumns: string[] = [], updateCache = true): KnockoutComputed<Array<T>> {
        if (!storageColumnDefinitionKey) {
            return ko.computed(() => []);
        }

        const columnCache = StorageHelper.bind<Array<T>>(storageColumnDefinitionKey, columns);
        const cachedValues = ko.unwrap(columnCache);
        const mergedColumns = ListViewHelper.mergeColumnDefinitions(cachedValues, columns, extendedColumns);

        if (updateCache) {
            columnCache(mergedColumns);
            return columnCache;
        } else {
            return ko.computed(() => mergedColumns);
        }
    }

    /**
     * Mergt zwei Arrays an ColumnDTOs nach einem bestimmten Update-Mechanismus
     * @param cachedColumns
     * @param refreshedColumns
     */
    private static mergeColumnDefinitions<T extends CoreFrameDTOs.IColumnDTO>(cachedColumns: Array<T>, refreshedColumns: Array<T>, extendedColumns: string[]): Array<T> {
        const cachedColumnNames = cachedColumns.map(c => c.name);
        const refreshedColumnsNames = refreshedColumns.map(c => c.name);
        const newColumns = refreshedColumns.filter(c => !_.contains(cachedColumnNames, c.name));

        // Bei neuer Standard-Spalte Storage verwerfen und Standard-Konfiguration übernehmen
        if (newColumns.filter(c => c.isVisible).length > 0) {
            return refreshedColumns;
        }

        // Alte Spalten entfernen
        const deprecatedColumnNames = cachedColumnNames.filter(c => !_.contains(refreshedColumnsNames, c) && !_.contains(extendedColumns, c));

        const mergedColumns = cachedColumns.filter(c => !_.contains(deprecatedColumnNames, c.name));

        // Bestehende Spalten updaten
        for (let i = 0; i < mergedColumns.length; i++) {
            const refreshedColumn = refreshedColumns.filter(c => c.name === mergedColumns[i].name);
            mergedColumns[i] = ListViewHelper.mergeColumnDefiniton(mergedColumns[i], _.first(refreshedColumn));
        }

        // Neue optionale Spalten hinzufügen
        newColumns.forEach(newColumn => {
            mergedColumns.push(_.first(refreshedColumns.filter(c => c.name === newColumn.name)));
        });

        return mergedColumns;
    }

    /**
     * Setzt die Sichtbarkeit der bestehenden Column in die aktualisierte Column vom Server
     * @param cachedColumn
     * @param refreshedColumn
     */
    private static mergeColumnDefiniton<T extends CoreFrameDTOs.IColumnDTO>(cachedColumn: T, refreshedColumn: T): T {
        if (Utilities.isNullOrUndefined(refreshedColumn))
            return cachedColumn;
        const clone: T = JSON.parse(JSON.stringify(refreshedColumn));

        clone.isVisible = cachedColumn.isVisible;

        return clone;
    }

    /**
     * Erzeugt einen zusammengesetzten String aus sichtbaren und nicht sichtbaren Spalten, der in USP gespeichert wird
     * @param visibleColumnNames
     * @param invisibleColumnNames
     */
    public static createColumnDefinitionString(visibleColumnNames: string[], invisibleColumnNames: string[]): string {
        const visibleString = ListViewHelper.createColumnString(visibleColumnNames);
        const invisibleString = ListViewHelper.createColumnString(invisibleColumnNames);
        return visibleString + '$|$' + invisibleString;
    }

    /**
     * Erzeugt den String der Spalten
     * @param columnNames
     */
    public static createColumnString(columnNames: string[]): string {
        return columnNames
            .filter((s) => {
                return !String.isNullOrEmpty(s);
            })
            .map((x) => {
                // Escape String, wenn CollumnName Trennsymbol ',' enthält
                if (x.indexOf(',') >= 0)
                    return '$$[' + x + ']$$';
                return x;
            })
            .join(',');
    }

    public static buildExportLinkViewModel(
        exportType: string,
        visible: any,
        labelParams: any,
        imageParams: any,
        typeName: string,
        locator: KnockoutComputed<string>,
        onClickCallback: (exportType: string) => void): LinkViewModelT {

        const LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
        const ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
        const LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');

        return new LinkViewModel({
            parentLocator: locator,
            locator: typeName + ".toolbar.export_" + exportType,
            label: new LabelViewModel($.extend({
                ergName: exportType + '_EXPORT',
                parentLocator: locator,
                locator: typeName + ".toolbar.export_" + exportType + ".label"
            }, labelParams)),

            image: new ImageViewModel($.extend(imageParams, {
                parentLocator: locator,
                locator: typeName + ".toolbar.export_" + exportType + ".image",
                alternativeText: new LabelViewModel({
                    ergName: exportType + '_EXPORT_TOOLTIP'
                })
            })),

            tooltip: new LabelViewModel({
                ergName: exportType + '_EXPORT_TOOLTIP'
            }),

            isVisible: visible,
            isActive: false,
            isEnabled: true,
            onClick: () => {
                onClickCallback(exportType);
            }
        });
    }
}